import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Link,
  Divider,
  TextField,
  Tooltip,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material/styles';

// Styled components
const AdContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  maxWidth: 500,
  margin: '0 auto',
}));

const AdHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const AdContent = styled(Box)(({ theme }) => ({
  '& img': {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
}));

const AdFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
}));

const AdActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 0),
}));

const ActionButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  cursor: 'pointer',
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const EditableText = ({ text, onChange, multiline = false, maxLength, label }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(text);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    onChange(value);
  };

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  // Calculate character count status
  const getCharCountColor = () => {
    if (!maxLength) return 'text.secondary';
    
    // For maxLength=0, use 25 as a recommended length for descriptions
    const effectiveMaxLength = maxLength === 0 ? 25 : maxLength;
    const ratio = value.length / effectiveMaxLength;
    
    if (ratio >= 1) return 'error.main';
    if (ratio >= 0.9) return 'warning.main';
    return 'text.secondary';
  };

  if (isEditing) {
    return (
      <Box sx={{ position: 'relative' }}>
        <TextField
          fullWidth
          multiline={multiline}
          value={value}
          onChange={handleChange}
          variant="outlined"
          size="small"
          autoFocus
          onBlur={handleSave}
          inputProps={{ maxLength: maxLength > 0 ? maxLength : undefined }}
          error={maxLength > 0 && value.length >= maxLength}
          helperText={
            maxLength ? (
              <Typography
                variant="caption"
                sx={{ color: getCharCountColor() }}
              >
                {value.length}/{maxLength === 0 ? '25 (recommended)' : maxLength} characters
                {maxLength > 0 && value.length >= maxLength && ' (maximum reached)'}
                {maxLength === 0 && value.length > 25 && ' (recommendation exceeded)'}
              </Typography>
            ) : null
          }
        />
        <IconButton
          size="small"
          onClick={handleSave}
          sx={{ position: 'absolute', right: -40, top: '50%', transform: 'translateY(-50%)' }}
        >
          <CheckIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', '&:hover .edit-icon': { opacity: 1 } }}>
      <Typography 
        variant={multiline ? "body2" : "subtitle2"} 
        sx={{ pr: 4 }}
        color={(maxLength > 0 && value.length > maxLength) || 
              (maxLength === 0 && value.length > 25) ? 'error.main' : 'text.primary'}
      >
        {text}
        {maxLength > 0 && value.length > maxLength && (
          <Typography component="span" variant="caption" color="error.main" sx={{ ml: 1 }}>
            (exceeds limit)
          </Typography>
        )}
        {maxLength === 0 && value.length > 25 && (
          <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
            (exceeds recommendation)
          </Typography>
        )}
      </Typography>
      <Tooltip title={`Edit ${label}`}>
        <IconButton
          size="small"
          onClick={handleEdit}
          className="edit-icon"
          sx={{
            position: 'absolute',
            right: -40,
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const FacebookAdPreview = ({ adData, onUpdate }) => {
  const { copy, image } = adData;

  const handleCopyChange = (field) => (newValue) => {
    onUpdate({
      ...adData,
      copy: {
        ...copy,
        [field]: newValue
      }
    });
  };

  return (
    <AdContainer>
      {/* Ad Header */}
      <AdHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar src="/facebook-page-avatar.png" />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Your Page Name
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sponsored · <Link href="#" underline="hover">Learn More</Link>
            </Typography>
          </Box>
        </Box>
        <IconButton size="small">
          <MoreHorizIcon />
        </IconButton>
      </AdHeader>

      {/* Ad Primary Text */}
      <Box sx={{ px: 2, pb: 1 }}>
        <EditableText
          text={copy.primaryText}
          onChange={handleCopyChange('primaryText')}
          multiline
          maxLength={125}
          label="Primary Text"
        />
      </Box>

      {/* Ad Image */}
      <AdContent>
        {image.url.includes('placeholder-image') ? (
          <Box 
            sx={{ 
              height: 300, 
              bgcolor: 'action.hover', 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center',
              color: 'text.secondary'
            }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              No image available
            </Typography>
            <Typography variant="caption">
              Click the image icon to generate or select an image
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            <img src={image.url} alt="Ad" style={{ width: '100%', height: 'auto', display: 'block' }} />
            {/* Edited indicator */}
            {image.edited && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  bgcolor: 'success.main',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}
              >
                Edited
              </Box>
            )}
          </Box>
        )}
      </AdContent>

      {/* Ad Headline and Description */}
      <AdFooter>
        <Box sx={{ bgcolor: 'background.default', p: 1, borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" component="div">
            yourwebsite.com
          </Typography>
          <EditableText
            text={copy.headline}
            onChange={handleCopyChange('headline')}
            maxLength={0} // 0 means no hard limit, but still show the recommendation of 40 chars
            label="Headline (recommended: 40 chars)"
          />
          <EditableText
            text={copy.description}
            onChange={handleCopyChange('description')}
            maxLength={0} // 0 means no hard limit, but still show the recommendation of 25 chars
            label="Description (recommended: 25 chars)"
          />
        </Box>

        {/* Engagement Metrics */}
        <Box sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            42 Likes
          </Typography>
          <Typography variant="caption" color="text.secondary">
            •
          </Typography>
          <Typography variant="caption" color="text.secondary">
            12 Comments
          </Typography>
        </Box>

        <Divider />

        {/* Action Buttons */}
        <AdActions>
          <ActionButton>
            <ThumbUpOutlinedIcon fontSize="small" />
            <Typography variant="body2">Like</Typography>
          </ActionButton>
          <ActionButton>
            <ChatBubbleOutlineOutlinedIcon fontSize="small" />
            <Typography variant="body2">Comment</Typography>
          </ActionButton>
          <ActionButton>
            <ShareOutlinedIcon fontSize="small" />
            <Typography variant="body2">Share</Typography>
          </ActionButton>
        </AdActions>
      </AdFooter>
    </AdContainer>
  );
};

export default FacebookAdPreview; 